import React, { useState } from 'react';
import { X } from 'lucide-react';
import classes from '../glossary.module.scss';
import { createCn } from '../../../helpers/utils';

const cn = createCn('glossary', classes);

interface GlossaryChipsInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
}

/** Ввод списка строк чипсами: добавление по Enter/запятой, удаление — крестиком на чипсе.
 *  Используется для aliases термина глоссария (варианты ошибочного распознавания). */
const GlossaryChipsInput = ({
  values,
  onChange,
  disabled,
  placeholder,
  'aria-label': ariaLabel,
}: GlossaryChipsInputProps) => {
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    const trimmed = draft.trim();
    setDraft('');
    if (!trimmed) return;
    if (values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) return;
    onChange([...values, trimmed]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const removeAt = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div className={cn('chips-input')} onClick={(e) => {
      if (e.target === e.currentTarget) {
        (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus();
      }
    }}
    >
      {values.map((value, index) => (
        <span key={`${value}-${index}`} className={cn('chip')}>
          {value}
          <button
            type="button"
            className={cn('chip-remove')}
            onClick={() => removeAt(index)}
            disabled={disabled}
            aria-label={`Удалить вариант «${value}»`}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        type="text"
        className={cn('chips-field')}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
        disabled={disabled}
        placeholder={values.length === 0 ? placeholder : ''}
        aria-label={ariaLabel}
      />
    </div>
  );
};

export default GlossaryChipsInput;
